import { supabase } from '@/lib/supabase';

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  premiumUsers: number;
  affiliateReferrals: number;
  growthRate: number;
}

export interface UserData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  registrationDate: string;
  lastLogin?: string;
  subscriptionPlan: 'free' | 'premium' | 'enterprise';
  subscriptionStatus: 'active' | 'expired' | 'cancelled';
  subscriptionExpiry?: string;
  aiMessagesUsed: number;
  marketAnalysisUsage: number;
  source: 'organic' | 'affiliate';
  affiliateName?: string;
  isActive: boolean;
  isAdmin: boolean;
}

export interface UserFilters {
  search?: string;
  subscriptionStatus?: string;
  activityStatus?: string;
  source?: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
}

export const userManagementService = {
  async getUserStats(): Promise<UserStats> {
    try {
      // Get total users from user_profiles (this is the main user table)
      const { count: totalUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      // Get active users (users with sessions in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: activeSessions } = await supabase
        .from('assistant_sessions')
        .select('user_id', { count: 'exact' })
        .gte('updated_at', thirtyDaysAgo.toISOString());

      const activeUserIds = new Set(activeSessions?.map(s => s.user_id) || []);
      const activeUsers = activeUserIds.size;

      // Get premium users (active subscriptions)
      const { count: premiumUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'active')
        .in('subscription_plan', ['premium', 'enterprise']);

      // Get affiliate referrals
      const { count: affiliateReferrals } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .not('referred_by_affiliate', 'is', null);

      // Calculate growth rate (users from last 30 days vs previous 30 days)
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

      const { count: recentUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { count: previousUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const growthRate = previousUsers && previousUsers > 0 ? ((recentUsers || 0) - previousUsers) / previousUsers * 100 : 0;

      return {
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        premiumUsers: premiumUsers || 0,
        affiliateReferrals: affiliateReferrals || 0,
        growthRate: Math.round(growthRate * 100) / 100
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        premiumUsers: 0,
        affiliateReferrals: 0,
        growthRate: 0
      };
    }
  },

  async getUsers(
    page: number = 1,
    pageSize: number = 50,
    filters: UserFilters = {}
  ): Promise<{ users: UserData[]; totalCount: number }> {
    try {
      // Query from user_profiles as the main table since it has all the data we need
      let query = supabase
        .from('user_profiles')
        .select(`
          user_id,
          display_name,
          display_email,
          subscription_plan,
          subscription_status,
          referred_by_affiliate,
          is_admin,
          created_at,
          updated_at,
          affiliates!referred_by_affiliate (
            name
          )
        `, { count: 'exact' });

      // Apply filters
      if (filters.search) {
        query = query.or(`display_name.ilike.%${filters.search}%,display_email.ilike.%${filters.search}%`);
      }

      if (filters.subscriptionStatus && filters.subscriptionStatus !== 'all') {
        query = query.eq('subscription_status', filters.subscriptionStatus);
      }

      if (filters.source && filters.source !== 'all') {
        if (filters.source === 'affiliate') {
          query = query.not('referred_by_affiliate', 'is', null);
        } else {
          query = query.is('referred_by_affiliate', null);
        }
      }

      if (filters.dateRange) {
        query = query
          .gte('created_at', filters.dateRange.from.toISOString())
          .lte('created_at', filters.dateRange.to.toISOString());
      }

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data: profilesData, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Get usage data for all users in batch
      const userIds = profilesData?.map(p => p.user_id) || [];
      
      // Get AI message usage
      const { data: messageUsage } = await supabase
        .from('user_message_usage')
        .select('user_id, message_count')
        .in('user_id', userIds);

      // Get market analysis usage
      const { data: analysisUsage } = await supabase
        .from('ai_analysis_sessions')
        .select('user_id')
        .in('user_id', userIds);

      // Get last login from assistant sessions
      const { data: lastSessions } = await supabase
        .from('assistant_sessions')
        .select('user_id, updated_at')
        .in('user_id', userIds)
        .order('updated_at', { ascending: false });

      // Create lookup maps for efficiency
      const messageUsageMap = new Map(
        messageUsage?.reduce((acc, usage) => {
          const existing = acc.get(usage.user_id) || 0;
          acc.set(usage.user_id, existing + usage.message_count);
          return acc;
        }, new Map()) || []
      );

      const analysisUsageMap = new Map(
        analysisUsage?.reduce((acc, session) => {
          const existing = acc.get(session.user_id) || 0;
          acc.set(session.user_id, existing + 1);
          return acc;
        }, new Map()) || []
      );

      const lastLoginMap = new Map(
        lastSessions?.reduce((acc, session) => {
          if (!acc.has(session.user_id)) {
            acc.set(session.user_id, session.updated_at);
          }
          return acc;
        }, new Map()) || []
      );

      // Filter by activity status if needed
      let filteredProfiles = profilesData || [];
      if (filters.activityStatus && filters.activityStatus !== 'all') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        filteredProfiles = profilesData?.filter(profile => {
          const lastLogin = lastLoginMap.get(profile.user_id);
          const isActive = lastLogin && new Date(lastLogin) > thirtyDaysAgo;
          return filters.activityStatus === 'active' ? isActive : !isActive;
        }) || [];
      }

      const users: UserData[] = filteredProfiles.map(profile => {
        const affiliate = Array.isArray(profile.affiliates) ? profile.affiliates[0] : profile.affiliates;
        const lastLogin = lastLoginMap.get(profile.user_id);
        
        // Determine if user is active (activity in last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const isActive = lastLogin && new Date(lastLogin) > thirtyDaysAgo;
        
        return {
          id: profile.user_id,
          name: profile.display_name || 'Unknown',
          email: profile.display_email || '',
          registrationDate: profile.created_at,
          lastLogin: lastLogin,
          subscriptionPlan: profile.subscription_plan || 'free',
          subscriptionStatus: profile.subscription_status || 'active',
          aiMessagesUsed: messageUsageMap.get(profile.user_id) || 0,
          marketAnalysisUsage: analysisUsageMap.get(profile.user_id) || 0,
          source: profile.referred_by_affiliate ? 'affiliate' : 'organic',
          affiliateName: affiliate?.name,
          isActive: isActive || false,
          isAdmin: profile.is_admin || false
        };
      });

      // Apply activity filter after getting data (since we can't easily do this in SQL)
      let finalUsers = users;
      if (filters.activityStatus && filters.activityStatus !== 'all') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        finalUsers = users.filter(user => {
          return filters.activityStatus === 'active' ? user.isActive : !user.isActive;
        });
      }

      return {
        users: finalUsers,
        totalCount: count || 0
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { users: [], totalCount: 0 };
    }
  },

  async getUserById(id: string): Promise<UserData | null> {
    try {
      // Get user profile data
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select(`
          user_id,
          display_name,
          display_email,
          subscription_plan,
          subscription_status,
          referred_by_affiliate,
          is_admin,
          created_at,
          updated_at,
          affiliates!referred_by_affiliate (
            name
          )
        `)
        .eq('user_id', id)
        .single();

      if (error) throw error;
      if (!profile) return null;

      // Get usage data
      const [messageUsage, analysisUsage, lastSession] = await Promise.all([
        supabase
          .from('user_message_usage')
          .select('message_count')
          .eq('user_id', id),
        supabase
          .from('ai_analysis_sessions')
          .select('id')
          .eq('user_id', id),
        supabase
          .from('assistant_sessions')
          .select('updated_at')
          .eq('user_id', id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .single()
      ]);

      const totalMessages = messageUsage.data?.reduce((sum, usage) => sum + usage.message_count, 0) || 0;
      const totalAnalysis = analysisUsage.data?.length || 0;
      const lastLogin = lastSession.data?.updated_at;

      // Determine if user is active (activity in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const isActive = lastLogin && new Date(lastLogin) > thirtyDaysAgo;

      const affiliate = Array.isArray(profile.affiliates) ? profile.affiliates[0] : profile.affiliates;

      return {
        id: profile.user_id,
        name: profile.display_name || 'Unknown',
        email: profile.display_email || '',
        registrationDate: profile.created_at,
        lastLogin: lastLogin,
        subscriptionPlan: profile.subscription_plan || 'free',
        subscriptionStatus: profile.subscription_status || 'active',
        aiMessagesUsed: totalMessages,
        marketAnalysisUsage: totalAnalysis,
        source: profile.referred_by_affiliate ? 'affiliate' : 'organic',
        affiliateName: affiliate?.name,
        isActive: isActive || false,
        isAdmin: profile.is_admin || false
      };
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      return null;
    }
  },

  async updateUserStatus(userId: string, isActive: boolean): Promise<boolean> {
    try {
      // Since we don't have an 'is_active' field in user_profiles, 
      // we'll simulate deactivation by updating subscription status
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          subscription_status: isActive ? 'active' : 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating user status:', error);
      return false;
    }
  },

  async updateUserProfile(userId: string, data: Partial<{
    displayName: string;
    displayEmail: string;
    subscriptionPlan: string;
    subscriptionStatus: string;
    isAdmin: boolean;
  }>): Promise<boolean> {
    try {
      const updateData: any = { updated_at: new Date().toISOString() };
      
      if (data.displayName !== undefined) updateData.display_name = data.displayName;
      if (data.displayEmail !== undefined) updateData.display_email = data.displayEmail;
      if (data.subscriptionPlan !== undefined) updateData.subscription_plan = data.subscriptionPlan;
      if (data.subscriptionStatus !== undefined) updateData.subscription_status = data.subscriptionStatus;
      if (data.isAdmin !== undefined) updateData.is_admin = data.isAdmin;

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return false;
    }
  },

  async deleteUser(userId: string): Promise<boolean> {
    try {
      // Delete from user_profiles table (cascade will handle related data)
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      return false;
    }
  },

  async exportUsers(filters: UserFilters = {}): Promise<Blob> {
    try {
      const { users } = await this.getUsers(1, 10000, filters); // Get all users
      
      const csvData = [
        ['Name', 'Email', 'Registration Date', 'Last Login', 'Subscription Plan', 'Status', 'Source', 'Affiliate'],
        ...users.map(user => [
          user.name,
          user.email,
          new Date(user.registrationDate).toLocaleDateString(),
          user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never',
          user.subscriptionPlan,
          user.subscriptionStatus,
          user.source,
          user.affiliateName || ''
        ])
      ];

      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    } catch (error) {
      console.error('Error exporting users:', error);
      throw error;
    }
  }
};